/* eslint-disable spire/export-styles */
import useWindowSize from "@DSG/Common/Hooks/useWindowSize";
import colorPallete, { buttonFonts, displayHeading } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Img, { ImgProps } from "@insite/mobius/Img/Img";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    bannerType = "bannerType",
    overlayBackground = "overlayBackground",
    background = "background",
    backgroundColor = "backgroundColor",
    backgroundImage = "backgroundImage",
    imageAltText = "imageAltText",
    overlayContent = "overlayContent",
    topOverlayContent = "topOverlayContent",
    bottomOverlayContent = "bottomOverlayContent",
}

interface Props extends WidgetProps {
    fields: {
        [fields.bannerType]: string;
        [fields.overlayBackground]: string;
        [fields.background]: "image" | "color";
        [fields.backgroundColor]: string;
        [fields.backgroundImage]: string;
        [fields.imageAltText]: string;
        [fields.overlayContent]: string;
        [fields.topOverlayContent]: string;
        [fields.bottomOverlayContent]: string;
    };
}

export interface DsgCareerHeroBannerStyles {
    mainHeroBannerWrapper?: InjectableCss;
    bannerWrapper?: InjectableCss;
    bannerWrapperInner?: InjectableCss;
    bannerImageWrapper?: InjectableCss;
    overlayContentWrapper?: InjectableCss;
    topOverlayContentWrapper?: InjectableCss;
    bottomOverlayContentWrapper?: InjectableCss;
    img?: ImgProps;
}

const dsgCareerHeroBannerStyles: DsgCareerHeroBannerStyles = {
    mainHeroBannerWrapper: {
        css: css`
            margin-bottom: 24px;
            color: ${colorPallete.grey800};
            position: relative;
            p {
                color: ${colorPallete.grey800};
            }
            .mainHeading {
                width: 100%;
                h1 {
                    margin-bottom: 0;
                }
            }
          
            .displayHeading {
                font: ${displayHeading.displaySemiboldSm.fontWeight} ${displayHeading.displaySemiboldSm.fontSize}/${displayHeading.displaySemiboldSm.lineHeight} ${displayHeading.displaySemiboldSm.fontFamily};
            }
            .buttonWrapper {      
                gap: 24px 36px;
                display: flex;
                flex-wrap: wrap;
                justify-content: center;
                [class*="StyledButton"],
                [class*="StyledClickable"] {
                    & {
                        min-height: 38px;
                        padding: 5px 24px;
                        border-radius: 30px;
                        font: ${buttonFonts.buttonSm.fontWeight} ${buttonFonts.buttonSm.fontSize} /
                            ${buttonFonts.buttonSm.lineHeight} ${buttonFonts.buttonSm.fontFamily};
                        text-align: center;
                        justify-content: center;
                    }
                    span {
                        display: flex;
                        align-items: center;
                        &[class*="LazyImageStyle"] {
                            padding: 0 !important;
                            display: flex !important;
                        }
                    }
                    
                }
            }
            [class*="StyledButton"],
            [class*="StyledClickable"] {
                &,
                &:hover,
                &:focus,
                &:active {
                    span {
                        color: inherit;
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    .displayHeading {
                        font-size: clamp(24px, calc(24px + (38 - 24) * (100vw - 768px)/(1440 - 768)), 38px);
                        line-height: clamp(24px, calc(24px + (33 - 24) * (100vw - 768px)/(1440 - 768)), 33px);
                    }
                    .buttonWrapper { 
                        gap: 8px;
                    }
                `,
                css`
                    .buttonWrapper { 
                        [class*="StyledButton"],
                        [class*="StyledClickable"] {
                            padding: 6px 24px;
                            min-height: 48px;
                            border-radius: 30px;
                            font-size: ${buttonFonts.buttonLg.fontSize};
                            line-height: ${buttonFonts.buttonLg.lineHeight};
                            min-width: 205px;
                        }
                    }
                `
            ], "min")}
            
            @media screen and (min-width: 1200px) {
                .buttonWrapper { 
                    gap: 24px 36px;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                css`
                    .buttonWrapper {      
                        padding: 0 40px;
                        [class*="StyledButton"],
                        [class*="StyledClickable"] {
                            width: 100%;
                        }
                    }
                `,
            ], "max")}
        `
    },
    bannerWrapperInner: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            row-gap: 16px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    row-gap: 0;
                `,
            ], "min")}
        `
    },
    overlayContentWrapper: {
        css: css`
            &:empty {
                display: none;
            }
            > div {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                > [class*="TypographyStyle"] {
                    margin-bottom: 0;  
                    width: 100%;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    border-radius: 8px;
                    max-width: 968px;
                    margin: -80px 40px 0;
                    > div {
                        padding: 24px 48px;
                    }
                `,
            ], "min")}
        `
    },
    bannerWrapper: {
        css: css`
            &.multiCTABanner {
                .mainHeading {
                    padding: 16px;
                    border-radius: 8px;
                    text-align: center;
                }
                .displayHeading {
                    text-align: center;
                }
                .overlayBottom {
                    padding: 13px 14px;
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: center;
                    gap: 8px;
                    [class*="TypographyStyle"] {
                        margin-bottom: 0;
                    }
                    a {
                        > span {
                            display: flex;
                            column-gap: 8px;
                            align-items: center;
                            [class*="LazyImageStyle"] {
                                padding: 0 !important;
                                display: flex !important;
                            }
                        }
                    }
                   
                }
                img {
                    border-radius: 0;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    &.multiCTABanner {
                        margin: 0 -16px;
                        width: calc(100% + 32px);
                        margin-top: -24px;
                        .mainHeading {
                            border-radius: 0 8px 8px 0;
                            padding: 24px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            max-width: 250px;
                        }
                        h1 {
                            font-size: clamp(20px, calc(20px + (32 - 20) * (100vw - 768px)/(1440 - 768)), 32px);
                            line-height:clamp(24px,calc(24px + (42 - 24) * (100vw - 768px)/(1440 - 768)),42px);
                        }
                        .displayHeading {
                            max-width: 500px;
                            text-align: center;
                            padding: 12px;
                            width: 100%;
                            align-items: center;
                            display: flex;
                            justify-content: center;
                        }
                        .overlayBottom {
                            border-radius: 8px 8px 0 0;
                            padding: 14px 24px;
                            gap: 16px;
                        }
                    }
                    &.simpleHeroBanner {
                        h1 {
                            font-size: clamp(24px, calc(24px + (32 - 24) * (100vw - 768px)/(1440 - 768)), 32px) !important;
                            line-height:clamp(32px,calc(32px + (48 - 32) * (100vw - 768px)/(1440 - 768)), 48px) !important;
                        }
                    }           
                `,
                css`
                    &.multiCTABanner {
                        margin: 0 -24px;
                        width: calc(100% + 48px);
                        margin-top: -24px;
                        .mainHeading {
                            padding: 30px 42px;
                            max-width: 343px;
                        }
                        .displayHeading {
                            max-width: 900px;
                            padding: 16px 24px;
                        }
                    }           
                `
            ], "min")}
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                css`
                    &.multiCTABanner {
                        > div,
                        > div > div {
                            display: flex;
                            flex-direction: column;
                            gap: 24px;
                            width: 100%;
                        }
                        .overlayBottom {
                            flex-direction: column;
                            align-items: center;
                        }
                    }           
                `,
            ], "max")}
        `
    },
    topOverlayContentWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    position: absolute;
                    top: 0;
                    left: -16px;
                    right: -16px;
                    width: calc(100% + 32px);
                    display: flex;       
                    padding-right: 34px;   
                    align-items: center;
                `,
                css`
                    left: -24px;
                    right: -24px;
                    width: calc(100% + 48px);
                `
            ], "min")}
        `
    },
    bottomOverlayContentWrapper: {
        css: css`
            display: flex;   
            flex-direction: column; 
            gap: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    position: absolute;
                    bottom: 0;
                    left: -16px;
                    right: -16px;
                    width: calc(100% + 32px);
                    align-items: center;
                    padding: 0 20px; 
                `,
                css`
                    left: -24px;
                    right: -24px;
                    width: calc(100% + 48px);
                `
            ], "min")}
        `
    },
    img: {
        css: css`
            width: 100%;
            height: auto;
            display: block;
            border-radius: 8px;
        `
    }
};

const styles =  dsgCareerHeroBannerStyles;

export const DsgCareerHeroBanner = ({ fields }: Props) => {

    const { width } = useWindowSize();
    
    return (
        <StyledWrapper {...styles.mainHeroBannerWrapper}>
            {fields.bannerType === "offset-overlay-hero-banner" && (
                <StyledWrapper {...styles.bannerWrapper} className="simpleHeroBanner">
                    <StyledWrapper {...styles.bannerWrapperInner}  
                        style={{   
                            ...(fields.background === "color" ? {
                                backgroundColor:fields.backgroundColor, 
                            } : ''),
                        }} 
                    >
                        {fields.background === "image" && (
                            <Img src={fields.backgroundImage} {...styles.img} 
                                altText={fields.imageAltText || ''}
                            />
                        )}
                        <StyledWrapper {...styles.overlayContentWrapper}
                             style={{   
                                backgroundColor: fields.overlayBackground ? fields.overlayBackground : '#FFFFFF',
                            }} 
                        >
                            {parse(fields.overlayContent, parserOptions)}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}  
            {fields.bannerType === "multi-cta-hero-banner" && (
                <StyledWrapper {...styles.bannerWrapper} className="multiCTABanner">
                    <StyledWrapper {...styles.bannerWrapperInner}  
                        style={{   
                            ...(fields.background === "color" ? {
                                backgroundColor:fields.backgroundColor, 
                            } : ''),
                        }} 
                    >
                        {fields.topOverlayContent && (
                            <StyledWrapper {...styles.topOverlayContentWrapper}>
                                {parse(fields.topOverlayContent, parserOptions)}
                            </StyledWrapper>
                        )}
                        {fields.background === "image" && (
                            <Img src={fields.backgroundImage} {...styles.img} 
                                altText={fields.imageAltText || ''}
                            />
                        )}
                        {fields.bottomOverlayContent && (
                            <StyledWrapper {...styles.bottomOverlayContentWrapper}>
                                {parse(fields.bottomOverlayContent, parserOptions)}
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.bottomOverlayContentWrapper}>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
       
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: DsgCareerHeroBanner,
    definition: {
        group: "Common",
        displayName: "Career Hero Banners",
        fieldDefinitions: [
            {
                name: fields.bannerType,
                displayName:"Banner Type",
                editorTemplate: "RadioButtonsField",
                fieldType: "Contextual",
                defaultValue: "offset-overlay-hero-banner",
                sortOrder: 0,
                options: [
                    {
                        displayName: "Multi CTA Hero Banner",
                        value: "multi-cta-hero-banner",
                    },
                    {
                        displayName: "Offset Overlay Hero Banner",
                        value: "offset-overlay-hero-banner",
                    },
                ],
            },
            {
                name: fields.background,
                displayName: "Background",
                fieldType: "Contextual",
                editorTemplate: "RadioButtonsField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
            },
            {
                fieldType: "Contextual",
                name: fields.backgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "General",
                name: fields.imageAltText,
                editorTemplate: "ImagePickerField",
                defaultValue: "Career Hero Banner",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                name: "backgroundColor",
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                fieldType: "Contextual",
                defaultValue: "#f6f6f7",
                isVisible: widget => widget.fields.background === "color",
            },
            {
                name: fields.overlayBackground,
                displayName: "Background For Banner Content",
                editorTemplate: "ColorPickerField",
                defaultValue: "#FFFFFF",
                tab: basicTab,
                fieldType: "Contextual",
            },
            {
                name: fields.overlayContent,
                displayName: "Overlay content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                extendedConfig: { height: 200 },
                tab: basicTab,
                expandedToolbarButtons: {
                    moreMisc: {
                        buttons: [
                            "insertLink",
                            "insertImage",
                            "insertHR",
                            "specialCharacters",
                            "insertVideo",
                            "insertFile",
                        ],
                        buttonsVisible: 3,
                    },
                    code: {
                        buttons: ["selectAll", "html", "help"],
                        buttonsVisible: 3,
                    },
                },
                isVisible: widget => widget.fields.bannerType === 'offset-overlay-hero-banner',
            },
            {
                name: fields.topOverlayContent,
                displayName: "Top Section Overlay content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                extendedConfig: { height: 200 },
                tab: basicTab,
                expandedToolbarButtons: {
                    moreMisc: {
                        buttons: [
                            "insertLink",
                            "insertImage",
                            "insertHR",
                            "specialCharacters",
                            "insertVideo",
                            "insertFile",
                        ],
                        buttonsVisible: 3,
                    },
                    code: {
                        buttons: ["selectAll", "html", "help"],
                        buttonsVisible: 3,
                    },
                },
                isVisible: widget => widget.fields.bannerType === 'multi-cta-hero-banner',
            },
            {
                name: fields.bottomOverlayContent,
                displayName: "Bottom Section Overlay content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                extendedConfig: { height: 200 },
                tab: basicTab,
                expandedToolbarButtons: {
                    moreMisc: {
                        buttons: [
                            "insertLink",
                            "insertImage",
                            "insertHR",
                            "specialCharacters",
                            "insertVideo",
                            "insertFile",
                        ],
                        buttonsVisible: 3,
                    },
                    code: {
                        buttons: ["selectAll", "html", "help"],
                        buttonsVisible: 3,
                    },
                },
                isVisible: widget => widget.fields.bannerType === 'multi-cta-hero-banner',
            },
        ],
    },
};

export default widgetModule;